/**
 * 出口合规弹窗工具
 *
 * 放行规则（2026-09-07 确认）：
 *   - 仅合规判定「通过」才继续；
 *   - 命中 warn / review / blocked 一律阻断，软提示同样不放行；
 *   - 检测失败（200 + detectFailed）说明当前为 OPEN 策略、后端已落库，仅提示并继续；
 *   - 预检请求异常（300900 / reject）阻断并提示。
 *
 * 统一返回 Promise<boolean>：true 表示允许继续执行后续动作
 */

import { ExportComplianceServer } from '@/API/exportCompliance';
import { EC_TYPE, parsePrecheck, parseBusiness, pickReviewEvidence } from './exportCompliance';

/**
 * 预检链路：判读 + 弹窗
 * @param {Vue} vm
 * @param {Object} res - 预检接口原始响应
 * @param {Object} [options] - { processNode, originalBizId }
 * @returns {Promise<boolean>}
 */
export function confirmPrecheck (vm, res, options) {
    return showDialog(vm, parsePrecheck(res), Object.assign({ link: 'precheck' }, options || {}));
}

/**
 * 业务链路：判读 + 弹窗
 * @param {Vue} vm
 * @param {Object} res - 业务接口原始响应
 * @param {Object} [options] - { processNode, originalBizId, notifyError }
 *   notifyError：el-upload 直传不走 axios 拦截器，业务错误（非 11000）不会触发全局 toast；
 *   传 true 时由本工具 `$message.error(result.message)` 提示，避免「后端校验异常」静默。
 * @returns {Promise<boolean>}
 */
export function confirmBusiness (vm, res, options) {
    return showDialog(vm, parseBusiness(res), Object.assign({ link: 'business' }, options || {}));
}

function showDialog (vm, result, options) {
    switch (result.type) {
        case EC_TYPE.PASS:
            return Promise.resolve(true);
        case EC_TYPE.DETECT_FAILED:
            // 出现「200 + detectFailed」说明当前为 OPEN 策略（CLOSE 下检测失败为 300900，走 ERROR 分支阻断）。
            // 预检链路：尚未提交，给「继续 / 取消」选择，交由业务接口最终把关；
            // 业务链路：后端已落库，仅提示并放行，避免前端阻断造成前后端状态不一致。
            if (options.link === 'precheck') {
                return notifyDetectFailedPrecheck(vm);
            }
            return notifyDetectFailedPass(vm);
        case EC_TYPE.WARN:
            // 只要合规判定未通过即阻断（含软提示），不放行
            return alertOnly(vm, result);
        case EC_TYPE.REVIEW:
        case EC_TYPE.BLOCKED:
            return showReviewDialog(vm, result, options);
        case EC_TYPE.ERROR:
        default:
            // 预检链路：300900 等异常统一弹「服务异常」提示。
            if (options.link === 'precheck') {
                return alertOnly(vm, result);
            }
            // 业务链路：axios 通路（活动提交 / 保存 / 报备等）的业务错误码已由全局拦截器 toast，此处静默返回 false；
            // el-upload 直传不走拦截器，调用方需传 notifyError 显式提示，避免「后端校验异常」完全无反馈。
            if (options.notifyError && result.message) {
                vm.$message.error(result.message);
            }
            return Promise.resolve(false);
    }
}

function showReviewDialog (vm, result, options) {
    const evidence = pickReviewEvidence(result);
    // 无检测凭证（如 11000 但缺少 compliance / verdict）时无法发起审查，直接仅提示，不显示【发起审查】按钮
    if (!evidence) {
        return alertOnly(vm, result);
    }
    return vm.$confirm(buildContent(vm, result), vm.$t('notic.FXINFO'), {
        confirmButtonText: vm.$t('exportCompliance.startReview'),
        cancelButtonText: vm.$t('btn.Close'),
        type: 'warning',
        dangerouslyUseHTMLString: true
    }).then(() => {
        return createReview(vm, evidence, result, options).then(() => false);
    }).catch(() => false);
}

function createReview (vm, evidence, result, options) {
    const params = {
        processNode: options.processNode || (result.compliance && result.compliance.processNode) || '',
        originalBizId: options.originalBizId || ''
    };
    // 文档约定 engineLogId 与 detectionId 二选一，优先 engineLogId（logId）
    if (evidence.engineLogId) {
        params.engineLogId = evidence.engineLogId;
    } else if (evidence.detectionId) {
        params.detectionId = evidence.detectionId;
    }
    if (vm.$store && vm.$store.commit) {
        vm.$store.commit('setSubPageLoading', true);
    }
    const closeLoading = () => {
        if (vm.$store && vm.$store.commit) {
            vm.$store.commit('setSubPageLoading', false);
        }
    };
    return ExportComplianceServer.reviewCreate(params).then(res => {
        closeLoading();
        const data = res && res.data;
        if (res && res.status === '200' && data) {
            // 优先展示合规平台统一返回的提示文案 promptMessage
            const promptMessage = data.promptMessage || '';
            if (promptMessage) {
                return vm.$alert(
                    escapeHtml(promptMessage),
                    vm.$t('exportCompliance.reviewCreated'),
                    { confirmButtonText: vm.$t('btn.OK') }
                ).catch(() => {});
            }
            // 兜底：无 promptMessage 时展示审查单号
            const no = data.interceptNo || data.reviewId || '';
            return vm.$alert(
                vm.$t('exportCompliance.interceptNo') + '：' + escapeHtml(no),
                vm.$t('exportCompliance.reviewCreated'),
                { confirmButtonText: vm.$t('btn.OK') }
            ).catch(() => {});
        }
        vm.$message.error((res && res.err) || vm.$t('exportCompliance.detectError'));
    }).catch(() => {
        closeLoading();
        vm.$message.error(vm.$t('exportCompliance.detectError'));
    });
}

/**
 * 检测失败且后端已放行的提示（仅 OPEN 策略的业务链路会出现）
 * 此时后端已落库，无论用户如何操作都返回 true，避免前端与后端状态不一致
 * @param {Vue} vm
 * @returns {Promise<boolean>} 恒为 true
 */
export function notifyDetectFailedPass (vm) {
    return vm.$alert(vm.$t('exportCompliance.detectFailedPass'), vm.$t('notic.FXINFO'), {
        confirmButtonText: vm.$t('btn.Close'),
        type: 'warning'
    }).then(() => true).catch(() => true);
}

/**
 * 预检链路检测失败的提示（仅 OPEN 策略会出现）
 * 此时尚未提交，给「继续 / 取消」选择，由业务接口最终把关
 * @param {Vue} vm
 * @returns {Promise<boolean>} 用户选择继续返回 true，取消返回 false
 */
export function notifyDetectFailedPrecheck (vm) {
    return vm.$confirm(vm.$t('exportCompliance.detectFailedPrecheck'), vm.$t('notic.FXINFO'), {
        confirmButtonText: vm.$t('btn.OK'),
        cancelButtonText: vm.$t('btn.Cancel'),
        type: 'warning'
    }).then(() => true).catch(() => false);
}

function alertOnly (vm, result) {
    const isError = result.type === EC_TYPE.ERROR;
    const content = isError ? vm.$t('exportCompliance.detectError') : buildContent(vm, result);
    return vm.$alert(content, vm.$t('notic.FXINFO'), {
        confirmButtonText: vm.$t('btn.Close'),
        type: isError ? 'error' : 'warning',
        dangerouslyUseHTMLString: true
    }).then(() => false).catch(() => false);
}

/**
 * 预检请求失败（网络异常 / Promise reject）时的统一提示
 * 按 Q1 决策：阻断，不执行后续动作
 * @param {Vue} vm
 * @returns {Promise<boolean>} 恒为 false
 */
export function notifyDetectFailed (vm) {
    return vm.$alert(vm.$t('exportCompliance.detectError'), vm.$t('notic.FXINFO'), {
        confirmButtonText: vm.$t('btn.Close'),
        type: 'error'
    }).then(() => false).catch(() => false);
}

function buildContent (vm, result) {
    if (result.message) return escapeHtml(result.message);
    return vm.$t('exportCompliance.warnDefault');
}

function escapeHtml (str) {
    return String(str === undefined || str === null ? '' : str)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        // 白名单放行 <br> 标签（支持 <br>、<br/>、<br />），其他标签仍转义以防 XSS
        .replace(/&lt;br\s*\/?&gt;/g, '<br>');
}
